// frontend/src/services/groupDashboardService.js
import axios from 'axios';
import { toast } from 'react-toastify';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

class GroupDashboardService {
  constructor() {
    this.api = axios.create({
      baseURL: `${API_URL}/group-dashboard`,
      headers: {
        'Content-Type': 'application/json'
      }
    });
    
    // Add token to requests
    this.api.interceptors.request.use(config => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
    
    // Handle errors globally
    this.api.interceptors.response.use(
      response => response,
      error => {
        const message = error.response?.data?.error || error.message || 'Something went wrong';
        toast.error(message);
        return Promise.reject(error);
      }
    );
  }

  // ========== EXPENSE MANAGEMENT ==========
  
  async addExpense(groupId, expenseData) {
    try {
      const response = await this.api.post(`/${groupId}/expenses`, expenseData);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
  
  async getExpenses(groupId) {
    try {
      const response = await this.api.get(`/${groupId}/expenses`);
      return response.data.expenses;
    } catch (error) {
      throw error;
    }
  }
  
  async splitExpense(groupId, expenseId) {
    try {
      const response = await this.api.post(`/${groupId}/expenses/${expenseId}/split`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
  
  async getExpenseSummary(groupId) {
    try {
      const response = await this.api.get(`/${groupId}/expense-summary`);
      return response.data.summary;
    } catch (error) {
      throw error;
    }
  }
  
  async getMemberBalances(groupId) {
    try {
      const response = await this.api.get(`/${groupId}/balances`);
      return response.data.balances;
    } catch (error) {
      throw error;
    }
  }
  
  // ========== ROUTE PLANNING ==========
  
  async createRoutePlan(groupId, routeData) {
    try {
      const response = await this.api.post(`/${groupId}/route-plans`, routeData);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
  
  async getRoutePlans(groupId) {
    try {
      const response = await this.api.get(`/${groupId}/route-plans`);
      return response.data.routePlans;
    } catch (error) {
      throw error;
    }
  }
  
  async optimizeRoute(groupId, routeData) {
    try {
      const response = await this.api.post(`/${groupId}/optimize-route`, routeData);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
  
  async voteForRoutePlan(groupId, planId, voteType) {
    try {
      const response = await this.api.post(`/${groupId}/route-plans/${planId}/vote`, { voteType });
      return response.data;
    } catch (error) {
      throw error;
    }
  }
  
  // ========== ACTIVITIES MANAGEMENT ==========
  
  async addActivity(groupId, activityData) {
    try {
      const response = await this.api.post(`/${groupId}/activities`, activityData);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
  
  async getActivities(groupId) {
    try {
      const response = await this.api.get(`/${groupId}/activities`);
      return response.data.activities;
    } catch (error) {
      throw error;
    }
  }
  
  // ========== POLLS ==========
  
  async createPoll(groupId, pollData) {
    try {
      const response = await this.api.post(`/${groupId}/polls`, pollData);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
  
  async getPolls(groupId) {
    try {
      const response = await this.api.get(`/${groupId}/polls`);
      return response.data.polls;
    } catch (error) {
      throw error;
    }
  }
  
  async voteOnPoll(groupId, pollId, optionIndex) {
    try {
      const response = await this.api.post(`/${groupId}/polls/${pollId}/vote`, { optionIndex });
      return response.data;
    } catch (error) {
      throw error;
    }
  }
  
  // ========== DASHBOARD SUMMARY ==========
  
  async getDashboardSummary(groupId) {
  try {
    console.log(`🔍 [Dashboard Service] Fetching summary for group: ${groupId}`);
    console.log(`🔍 [Dashboard Service] API URL: ${this.api.defaults.baseURL}/${groupId}/summary`);
    
    const response = await this.api.get(`/${groupId}/summary`);
    
    console.log('✅ [Dashboard Service] Summary loaded:', response.data);
    return response.data.summary;
    
  } catch (error) {
    console.error('❌ [Dashboard Service] Error loading dashboard summary:');
    console.error('❌ Error message:', error.message);
    console.error('❌ Response data:', error.response?.data);
    console.error('❌ Response status:', error.response?.status);
    console.error('❌ Response headers:', error.response?.headers);
    
    throw error;
  }
}
  
  async getGroupStats(groupId) {
    try {
      const response = await this.api.get(`/${groupId}/stats`);
      return response.data.stats;
    } catch (error) {
      throw error;
    }
  }

  // Add these methods to your existing groupDashboardService.js

async addAccommodation(groupId, accommodationData) {
  try {
    const response = await this.api.post(`/${groupId}/accommodations`, accommodationData);
    return response.data;
  } catch (error) {
    throw error;
  }
}

async getAccommodations(groupId) {
  try {
    const response = await this.api.get(`/${groupId}/accommodations`);
    return response.data.accommodations;
  } catch (error) {
    throw error;
  }
}

async addTransportation(groupId, transportationData) {
  try {
    const response = await this.api.post(`/${groupId}/transportations`, transportationData);
    return response.data;
  } catch (error) {
    throw error;
  }
}

async getTransportations(groupId) {
  try {
    const response = await this.api.get(`/${groupId}/transportations`);
    return response.data.transportations;
  } catch (error) {
    throw error;
  }
}

async addChecklist(groupId, checklistData) {
  try {
    const response = await this.api.post(`/${groupId}/checklists`, checklistData);
    return response.data;
  } catch (error) {
    throw error;
  }
}

async getChecklists(groupId) {
  try {
    const response = await this.api.get(`/${groupId}/checklists`);
    return response.data.checklists;
  } catch (error) {
    throw error;
  }
}

async updateChecklistItem(groupId, checklistId, updateData) {
  try {
    const response = await this.api.put(`/${groupId}/checklists/${checklistId}`, updateData);
    return response.data;
  } catch (error) {
    throw error;
  }
}
}



export default new GroupDashboardService();